import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getFaq } from '../lib/cms';

export default function PraktiskInfo() {
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    getFaq().then(setFaqs);
  }, []);
  return (
    <div className="bg-[#090a0f] text-slate-100 min-h-screen pb-20">
      <Helmet>
        <title>Praktisk info - Foreningen ØrBike</title>
        <meta
          name="description"
          content="Alt det praktiske til ØrBike i Ørbæk Midtpunkt: rutevejledning fra Sjælland og Jylland, gratis parkering, pedaler (Look/SPD), klamper og tidsplan."
        />
        <meta property="og:title" content="Praktisk info - Foreningen ØrBike" />
        <meta property="og:description" content="Find vej, parkering, pedaler og tider til Danmarks største bike fest." />
      </Helmet>

      {/* Hero Header */}
      <section className="relative pt-36 sm:pt-40 pb-16 sm:pb-20 bg-gradient-to-b from-[#151224] to-[#090a0f] border-b border-pink-500/20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-pink-500/20 text-pink-400 border border-pink-500/30 mb-4">
            Alt hvad du skal vide før eventet
          </span>
          <h1 className="text-4xl sm:text-6xl font-black font-display text-white uppercase tracking-tight">
            Praktisk Information & FAQs
          </h1>
          <p className="text-pink-300 font-display text-lg sm:text-2xl font-bold uppercase mt-2">
            Sådan finder du frem til ØrBike i Ørbæk Midtpunkt
          </p>
        </div>
      </section>

      <section className="py-16 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        {/* Hurtig Tjekliste boks */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-5 rounded-2xl bg-[#12141f] border border-pink-500/30">
            <div className="text-pink-400 text-2xl mb-2">
              <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
            </div>
            <h4 className="font-bold text-white font-display text-lg uppercase">Lokation</h4>
            <p className="text-xs text-slate-300 mt-1">Ørbæk Midtpunkt<br />Langemosevej 5, 5853 Ørbæk</p>
          </div>

          <div className="p-5 rounded-2xl bg-[#12141f] border border-purple-500/30">
            <div className="text-purple-400 text-2xl mb-2">
              <i className="fa-solid fa-square-parking" aria-hidden="true"></i>
            </div>
            <h4 className="font-bold text-white font-display text-lg uppercase">Parkering</h4>
            <p className="text-xs text-slate-300 mt-1">Gratis parkering ved hallen og skolen</p>
          </div>

          <div className="p-5 rounded-2xl bg-[#12141f] border border-pink-500/30">
            <div className="text-pink-400 text-2xl mb-2">
              <i className="fa-solid fa-bicycle" aria-hidden="true"></i>
            </div>
            <h4 className="font-bold text-white font-display text-lg uppercase">Klamper</h4>
            <p className="text-xs text-slate-300 mt-1">Look Delta & Shimano SPD pedaler</p>
          </div>

          <div className="p-5 rounded-2xl bg-[#12141f] border border-purple-500/30">
            <div className="text-purple-400 text-2xl mb-2">
              <i className="fa-solid fa-clock" aria-hidden="true"></i>
            </div>
            <h4 className="font-bold text-white font-display text-lg uppercase">Dørene åbner</h4>
            <p className="text-xs text-slate-300 mt-1">Kl. 07.30 (Spinning starter kl. 09.00)</p>
          </div>
        </div>

        {/* Rutevejledning */}
        <div className="bg-[#12141f] border border-slate-800 rounded-3xl p-8 sm:p-10 space-y-8">
          <div>
            <span className="text-xs uppercase tracking-widest font-bold text-pink-400 font-mono">
              Find vej til eventet
            </span>
            <h3 className="text-2xl sm:text-3xl font-black text-white font-display uppercase tracking-tight mt-1">
              Hvordan finder jeg frem til ØrBike?
            </h3>
            <p className="text-slate-300 text-sm mt-2">
              <strong>ØrBike afholdes i Ørbæk Midtpunkt</strong> – centralt placeret i hjertet af Ørbæk, med nem adgang fra både Fyn, Jylland og Sjælland.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Fra Sjælland */}
            <div className="p-6 rounded-2xl bg-slate-900/80 border border-pink-500/20 space-y-3">
              <div className="flex items-center gap-2 text-pink-400 font-bold font-display uppercase text-lg">
                <i className="fa-solid fa-car" aria-hidden="true"></i>
                <span>Hvis du kommer fra Sjælland</span>
              </div>
              <p className="text-xs text-slate-400">
                Ørbæk Midtpunkt ligger midt i Ørbæk, kun <strong>11 km fra Storebæltsbroen</strong>.
              </p>
              <ul className="space-y-2 text-xs text-slate-300 list-disc list-inside">
                <li>Kør ad motorvejen til afkørsel <strong>“Nyborg Vest”</strong></li>
                <li>Drej til venstre og følg skiltene mod <strong>Ørbæk</strong></li>
                <li>Ved kirken i Ørbæk drejer du <strong>til venstre</strong> – følg skiltet mod <strong>Ørbæk Midtpunkt</strong> ad <strong>Svendborgvej</strong></li>
                <li>Efter ca. <strong>400 meter</strong> drejer du <strong>til venstre ad Langemosevej</strong></li>
              </ul>
              <div className="pt-2 text-xs text-emerald-400 font-semibold">
                ✓ Du er nu fremme – parkeringspladsen ligger lige ved Ørbæk Midtpunkt.
              </div>
            </div>

            {/* Fra Jylland / Odense */}
            <div className="p-6 rounded-2xl bg-slate-900/80 border border-purple-500/20 space-y-3">
              <div className="flex items-center gap-2 text-purple-400 font-bold font-display uppercase text-lg">
                <i className="fa-solid fa-car" aria-hidden="true"></i>
                <span>Hvis du kommer fra Jylland / Odense</span>
              </div>
              <p className="text-xs text-slate-400">
                Hurtig og nem afkørsel direkte fra motorvej E20.
              </p>
              <ul className="space-y-2 text-xs text-slate-300 list-disc list-inside">
                <li>Følg E20 (motorvejen) til afkørsel <strong>“Nyborg Vest”</strong></li>
                <li>Ved kirken i Ørbæk drej <strong>til venstre</strong> og følg skiltet mod <strong>Ørbæk Midtpunkt</strong> ad <strong>Svendborgvej</strong></li>
                <li>Efter ca. <strong>400 meter</strong> drej <strong>til venstre ad Langemosevej</strong></li>
              </ul>
              <div className="pt-2 text-xs text-emerald-400 font-semibold">
                ✓ Du er fremme – velkommen til ØrBike!
              </div>
            </div>
          </div>
        </div>

        {/* Parkering */}
        <div className="p-8 rounded-3xl bg-gradient-to-r from-pink-950/30 via-slate-900 to-purple-950/30 border border-pink-500/30 flex flex-col md:flex-row items-center gap-6">
          <div className="w-16 h-16 rounded-2xl bg-pink-500/20 text-pink-400 flex items-center justify-center text-3xl shrink-0">
            <i className="fa-solid fa-square-parking" aria-hidden="true"></i>
          </div>
          <div className="space-y-1 text-center md:text-left">
            <h3 className="text-2xl font-black font-display text-white uppercase">Fri Parkering</h3>
            <p className="text-sm text-slate-300 leading-relaxed">
              Der er <strong>fri parkering</strong> på både hallens og skolens parkeringspladser – lige ved Ørbæk Midtpunkt. Kom gerne i god tid før den officielle velkomst kl. 08.45.
            </p>
          </div>
        </div>

        {/* Pedaler og klamper detaljer */}
        <div className="bg-[#12141f] border border-slate-800 rounded-3xl p-8 sm:p-10 space-y-6">
          <h3 className="text-2xl sm:text-3xl font-black text-white font-display uppercase tracking-tight">
            Pedaler, cykelsko og klamper
          </h3>
          <p className="text-slate-300 text-sm leading-relaxed">
            Hos ØrBike cykler vi på cykler udstyret med <strong>Look Classic</strong> og <strong>Shimano SPD</strong> pedaler. Det anbefales kraftigt, at du medbringer egne cykelsko – det giver både bedre komfort og en langt bedre oplevelse på cyklen.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
            <div className="p-5 rounded-xl bg-slate-900/90 border border-slate-800">
              <h4 className="font-bold text-pink-400 text-base font-display uppercase mb-1">
                Look Delta (Røde klamper)
              </h4>
              <p className="text-xs text-slate-300 leading-relaxed">
                De klassiske store klamper, som ofte bruges i spinninglokaler.
              </p>
            </div>

            <div className="p-5 rounded-xl bg-slate-900/90 border border-slate-800">
              <h4 className="font-bold text-purple-400 text-base font-display uppercase mb-1">
                Shimano SPD (Små metalklamper)
              </h4>
              <p className="text-xs text-slate-300 leading-relaxed">
                De små metal-klamper, som bruges til mountainbike og spinning.
              </p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 text-xs text-slate-300">
            <strong>Kondisko:</strong> Vi har ikke beslag til almindelige sko, men du er naturligvis velkommen til at medbringe et par kondisko eller lette sko til at bevæge dig rundt i hallerne – det er ofte mere behageligt end at gå i cykelsko uden for cyklen.
          </div>
        </div>

        {/* Google Maps link */}
        <div className="text-center p-8 rounded-3xl bg-slate-900/60 border border-slate-800 space-y-4">
          <h3 className="text-xl font-bold font-display text-white uppercase">
            Brug din GPS direkte til døren
          </h3>
          <p className="text-xs text-slate-400">
            Adresse til GPS: Langemosevej 5, 5853 Ørbæk
          </p>
          <div>
            <a
              href="https://maps.google.com/?q=Langemosevej+5,+5853+Ørbæk"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs uppercase tracking-wider transition-colors border border-slate-700"
            >
              <i className="fa-solid fa-map-location-dot text-pink-400" aria-hidden="true"></i>
              <span>Åbn rutevejledning i Google Maps</span>
            </a>
          </div>
        </div>

        {/* FAQ Sektion fra CMS */}
        {faqs && faqs.length > 0 && (
          <div className="bg-[#12141f] border border-pink-500/20 rounded-3xl p-8 sm:p-10 space-y-6">
            <div className="text-center max-w-2xl mx-auto space-y-2 mb-8">
              <span className="text-xs uppercase tracking-widest font-bold text-pink-400 font-mono">
                Spørgsmål og svar
              </span>
              <h3 className="text-2xl sm:text-3xl font-black text-white font-display uppercase tracking-tight">
                Ofte stillede spørgsmål
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {faqs.map((faq) => (
                <div key={faq.id || faq.spoergsmaal} className="p-6 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-2.5">
                  <h4 className="font-bold text-pink-400 font-display text-base uppercase flex items-start gap-2.5">
                    <i className="fa-solid fa-circle-question text-sm mt-0.5 shrink-0 text-pink-500" aria-hidden="true"></i>
                    <span>{faq.spoergsmaal}</span>
                  </h4>
                  <div
                    className="text-xs text-slate-300 leading-relaxed space-y-2"
                    dangerouslySetInnerHTML={{ __html: faq.svar }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
